import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { FloatingNotification, SidePanel } from '@wix/design-system';
import type { RecommendedTool } from '../../../services/recommendedTools/RecommendedTools';
import {
  RecommendedTools,
  BusinessAppSetupHeader,
  BusinessAppHeader,
  BusinessAppActionItem,
  AppSetupStep,
  Section,
  SetupSuccessState,
} from '../../components';
import {
  AUTOMATION_IDS,
  getAppManageActionDataHook,
  getBusinessAppDataHook,
} from '../../../constants/automationIds';
import { SETUP_SUCCESS_ANIMATION_DURATION } from '../../../constants/constants';
import { usePrevious } from '../../../hooks/usePrevious';

import type { RegularAppData } from '../../../services/appMarket/appMarket.types';
import type { AppSetup } from '../../../services/setup/AppSetup';
import type {
  AppAction,
  AppActions,
  AppPremiumBannerInfo,
} from '../../../services/platform/platform.types';

import type {
  DealerAppPageAction,
  DealerAppPageData,
} from '../../../services/dealer/dealer.types';
import { useBILogger } from '@wix/fe-essentials/react';
import { TRANSLATIONS } from '../../../constants/translations';
import styles from './businessApp.module.scss';
import { t } from '../../../../../essentials/i18n';
import { BI_EVENTS_IDS, BI_PARAMS } from '../../../constants/biEvents';
import { getBIReportData, getBusinessAppOrigin } from '../../../utils/biEvents';
import { BusinessAppOnOtherBranchesNotification } from '../../components/BusinessAppOnOtherBranchesNotification/BusinessAppOnOtherBranchesNotification';

export interface BusinessAppProps {
  app: RegularAppData;
  appSetup: AppSetup;
  recommendedTools: RecommendedTool[];
  dealerData?: DealerAppPageData;
  appActions: AppActions;
  premiumBannerInfo: AppPremiumBannerInfo | null;
  trigger?: string;
  onAddApp: () => void;
}

export const BusinessApp: React.FC<BusinessAppProps> = ({
  app,
  appSetup,
  recommendedTools,
  appActions,
  premiumBannerInfo,
  dealerData,
  trigger,
  onAddApp,
}) => {
  const logger = useBILogger();

  const manageActions = useMemo(() => {
    const { primary, secondary } = appActions;
    const primaryWithOverrides = mergePrimaryActions(
      primary,
      dealerData?.primaryAction1,
      dealerData?.primaryAction2,
    );
    return primaryWithOverrides.concat(secondary);
  }, [appActions, dealerData]);

  const [displayAppSetupSection, setDisplayAppSetupSection] = useState(
    !appSetup.isComplete,
  );
  const displayManageActions =
    manageActions.length > 0 && !app.isInstalledOnOtherBranches;

  const displayRecommendedTools = recommendedTools.length > 0;

  const biSetupStatus = JSON.stringify(
    appSetup.steps.map((step) => {
      return {
        actionName: step.title,
        status: step.status,
      };
    }),
  );

  const dismissAppSetupSuccessState = useCallback(
    () => setDisplayAppSetupSection(false),
    [],
  );

  const onSuccessAnimationAppeared = useCallback(() => {
    setTimeout(dismissAppSetupSuccessState, SETUP_SUCCESS_ANIMATION_DURATION);
  }, [dismissAppSetupSuccessState]);

  const onHeaderContextMenuOpen = useCallback(() => {
    logger.log(
      getBIReportData(BI_EVENTS_IDS.ACTION_CLICK, app.id, {
        section: BI_PARAMS.SECTIONS.THREE_DOT_MENU,
        action_id: BI_PARAMS.ACTION_ID.THREE_DOT_MENU,
        target_cta: BI_PARAMS.TARGET_CTA.BUTTON_CLICKED,
        setup_status: biSetupStatus,
      }),
    );
  }, [logger, app]);

  const onHeaderContextMenuActionClick = useCallback(
    (action: AppAction) => action.onClick(),
    [logger, app],
  );

  const onPremiumBannerUpgradeClick = useCallback(() => {
    premiumBannerInfo?.upgrade();

    logger.log(
      getBIReportData(BI_EVENTS_IDS.ACTION_CLICK, app.id, {
        section: BI_PARAMS.SECTIONS.UPGRADE,
        action_id: BI_PARAMS.ACTION_ID.UPGRADE,
        target_cta: BI_PARAMS.TARGET_CTA.BUTTON_CLICKED,
        setup_status: biSetupStatus,
      }),
    );
  }, [premiumBannerInfo, logger, app]);

  const onManageActionClick = useCallback(
    (action: any, index: number) => {
      action.onClick();

      logger.log(
        getBIReportData(BI_EVENTS_IDS.ACTION_CLICK, app.id, {
          section: BI_PARAMS.SECTIONS.MANAGE,
          action_id: action.id,
          target_cta: BI_PARAMS.TARGET_CTA.BUTTON_CLICKED,
          action_rank_on_section: index + 1,
          setup_status: biSetupStatus,
        }),
      );
    },
    [logger, app],
  );

  const nextSetupStep = appSetup.nextStep;
  const prevIsComplete = usePrevious(appSetup.isComplete);

  useEffect(() => {
    logger.log(
      getBIReportData(BI_EVENTS_IDS.TAB_LOAD, app.id, {
        trigger,
        is_setup_shown: displayAppSetupSection,
        is_setup_done: appSetup.isComplete,
        setup_status: biSetupStatus,
        is_manage_shown: displayManageActions,
        is_recommended_tools_shown: displayRecommendedTools,
      }),
    );
  }, []);

  useEffect(() => {
    // Explicitly checking for false to differentiate from `undefined`.
    if (appSetup.isComplete && prevIsComplete === false) {
      logger.log(
        getBIReportData(BI_EVENTS_IDS.SETUP_COMPLETED, app.id, {
          app_id: app.id,
        }),
      );
      onSuccessAnimationAppeared();
    }
  }, [app, appSetup, logger, onSuccessAnimationAppeared, prevIsComplete]);
  const appHeaderSection = (
    <div data-hook={getBusinessAppDataHook(app.id)}>
      {app.isInstalledOnOtherBranches && (
        <BusinessAppOnOtherBranchesNotification
          appId={app.id}
          appName={dealerData?.title || app.name}
          onAddApp={onAddApp}
        />
      )}
      <BusinessAppHeader
        appId={app.id}
        title={dealerData?.title || app.name}
        description={dealerData?.description || app.description}
        imageSrc={dealerData?.imageUrl || app.icon}
        generalAppActions={appActions.general}
        showDivider={!premiumBannerInfo}
        onContextMenuOpen={onHeaderContextMenuOpen}
        onContextMenuActionClick={onHeaderContextMenuActionClick}
        hideContextMenu={app.isInstalledOnOtherBranches}
        biSetupStatus={biSetupStatus}
      />

      {premiumBannerInfo && (
        <FloatingNotification
          type="premium"
          text={premiumBannerInfo.text}
          textButtonProps={{
            label: premiumBannerInfo.linkText,
            onClick: onPremiumBannerUpgradeClick,
          }}
          dataHook={AUTOMATION_IDS.MY_BUSINESS.APP_PREMIUM_BANNER}
          showCloseButton={false}
          fullWidth
        />
      )}
    </div>
  );

  const appSetupSection =
    displayAppSetupSection && !app.isInstalledOnOtherBranches ? (
      <>
        <Section
          dataHook={AUTOMATION_IDS.MY_BUSINESS.APP_SETUP.CONTAINER}
          heading={
            <BusinessAppSetupHeader
              appDefId={app.id}
              appName={app.name}
              stepsCompleted={appSetup.completedStepsCount}
              stepsTotal={appSetup.totalStepsCount}
              showProgress={!appSetup.isComplete}
            />
          }
        >
          <div
            className={`${styles.setupWrapper} ${
              appSetup.isComplete
                ? styles.setupCompleteHeight
                : styles.setupNotCompleteHeight
            }`}
          >
            <div className={appSetup.isComplete ? styles.hidden : ''}>
              {appSetup.steps.map((step, index) => (
                <AppSetupStep
                  appId={app.id}
                  key={step.id}
                  step={step}
                  isPriority={step === nextSetupStep}
                  showDivider={index !== appSetup.steps.length - 1}
                  biData={{ biSetupStatus, stepRank: index + 1 }}
                />
              ))}
            </div>

            <div
              className={`
              ${styles.setupSuccessContainer}
              ${
                appSetup.isComplete
                  ? styles.setupSuccessContainer
                  : styles.hidden
              }`}
            >
              <SetupSuccessState
                appName={app.name}
                dataHook={
                  AUTOMATION_IDS.MY_BUSINESS.APP_SETUP.SUCCESS_ANIMATION
                }
                onDismiss={dismissAppSetupSuccessState}
              />
            </div>
          </div>
        </Section>
      </>
    ) : null;
  const manageActionsSection = displayManageActions ? (
    <Section
      heading={t(TRANSLATIONS.MY_BUSINESS.HEADERS.MANAGE)}
      dataHook={AUTOMATION_IDS.MY_BUSINESS.APP_MANAGE_ACTION_CONTAINER}
    >
      {manageActions.map((action, index) => (
        <BusinessAppActionItem
          key={action.id}
          dataHook={getAppManageActionDataHook(action.id)}
          textDataHook={action.dataHook}
          title={action.title}
          iconName={action.iconName || ''}
          iconUrl={action.iconUrl}
          showDivider={index !== manageActions.length - 1}
          onClick={() => onManageActionClick(action, index)}
          buttonText={action.buttonText}
        />
      ))}
    </Section>
  ) : null;

  const recommendedToolSection = displayRecommendedTools ? (
    <>
      <RecommendedTools
        tools={recommendedTools}
        trigger={getBusinessAppOrigin(app.id)}
      />
    </>
  ) : null;

  return (
    <>
      <SidePanel.Content stretchVertically={false} noPadding>
        {appHeaderSection}
      </SidePanel.Content>

      <SidePanel.Content noPadding>
        {appSetupSection}

        {manageActionsSection}

        {recommendedToolSection}
      </SidePanel.Content>
    </>
  );
};

interface AppActionWithOverrides extends AppAction {
  iconUrl?: string;
}

function mergeDefaultAndDealerAction(
  defaultAction: AppAction,
  dealerAction?: DealerAppPageAction,
): AppActionWithOverrides {
  if (!dealerAction) {
    return defaultAction;
  }

  return {
    id: defaultAction.id,
    iconName: defaultAction.iconName,
    iconUrl: dealerAction?.iconUrl,
    title: defaultAction.title,
    onClick: dealerAction.onClick || defaultAction.onClick,
    dataHook: defaultAction.dataHook,
    buttonText: defaultAction.buttonText,
  };
}

function mergePrimaryActions(
  rawActions: AppAction[],
  dealerAction1?: DealerAppPageAction,
  dealerAction2?: DealerAppPageAction,
): AppActionWithOverrides[] {
  return [
    mergeDefaultAndDealerAction(rawActions[0], dealerAction1),
    mergeDefaultAndDealerAction(rawActions[1], dealerAction2),
  ].filter(Boolean);
}
